import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const PaymentHistory = ({ payments }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const navigate = useNavigate();

  const filteredPayments = useMemo(() => {
    return (
      payments?.filter(
        (payment) =>
          payment?.invoice?.invoice_number
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          payment?.reference_number
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          payment?.payment_method
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      ) || []
    );
  }, [payments, searchTerm]);

  const totalAmount = useMemo(() => {
    return filteredPayments.reduce(
      (sum, payment) => sum + Number(payment?.amount || 0),
      0
    );
  }, [filteredPayments]);

  const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPayments = filteredPayments.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "badge bg-success";
      case "pending":
        return "badge bg-warning text-dark";
      case "failed":
        return "badge bg-danger";
      case "refunded":
        return "badge bg-info";
      default:
        return "badge bg-secondary";
    }
  };

  return (
    <div className="container-fluid p-4">
      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-header">
              <h5 className="card-title mb-0">Payment Summary</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-6">
                  <p className="text-muted mb-1">Total Payments</p>
                  <h3 className="fw-bold">{filteredPayments?.length}</h3>
                </div>
                <div className="col-6">
                  <p className="text-muted mb-1">Total Amount</p>
                  <h3 className="fw-bold">
                    ₹{Number(totalAmount)?.toFixed(2)}
                  </h3>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment History Card */}
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <h5 className="card-title mb-0">Payment History</h5>
          </div>
        </div>
        <div className="card-body">
          {paginatedPayments.length === 0 ? (
            <div className="text-center py-5 text-muted">No payments found</div>
          ) : (
            <div className="row g-3">
              {paginatedPayments.map((payment) => (
                <div key={payment?.id} className="col-12">
                  <div className="card border">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start flex-wrap">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2 mb-2">
                            <h6 className="fw-bold mb-0">
                              ₹{Number(payment?.amount)?.toFixed(2)}
                            </h6>
                            <span className={getStatusColor(payment?.status)}>
                              {payment?.status?.toUpperCase()}
                            </span>
                          </div>
                          <p className="text-muted mb-1">
                            <strong>
                              {payment?.payment_method
                                ?.replace("_", " ")
                                ?.toUpperCase()}
                            </strong>{" "}
                            • Invoice #{payment?.invoice?.invoice_number} • Room{" "}
                            {payment?.invoice?.reservation?.room_number
                              ?.room_number || "N/A"}
                          </p>
                          <p className="text-muted small mb-0">
                            {payment?.payment_date
                              ? new Date(
                                  payment.payment_date
                                ).toLocaleDateString()
                              : "Date N/A"}{" "}
                            •
                            {payment?.reference_number &&
                              ` Ref: ${payment.reference_number}`}
                          </p>
                        </div>
                        <div className="mt-2 mt-md-0">
                          <button
                            onClick={() => {
                              const paymentInvoice = payment?.invoice;
                              navigate("/reservation/invoice", {
                                state: { paymentInvoice },
                              });
                            }}
                            className="btn btn-outline-primary btn-sm"
                          >
                            <i className="me-2">📥</i>
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <nav className="mt-4">
              <ul className="pagination justify-content-center">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                <li className="page-item active">
                  <span className="page-link">
                    Page {currentPage} of {totalPages}
                  </span>
                </li>
                <li
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentHistory;
